import { BillableIcon, Currency, Field, Hours, Table } from '~/components';
import { TableBoxRowActions } from '~/components/table';
import { Formik } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { weights } from '~/styles';
import { calculateRevenue, emptyStringToNull, mergeValues } from '~/utils';
import * as Yup from 'yup';
import { MonthlyBudgetDrawerContext } from './MonthlyBudgetDrawerContext';
import calculateMonthlyBudget from './calculateMonthlyBudget';

function MonthlyBudgetMembersTable({ project, onChange }) {
  const [editIndex, setEditIndex] = useState(null);

  const handleSave = (value) => {
    const members = project.members.map((pm) =>
      pm.id === value.id
        ? emptyStringToNull({
            ...pm,
            estimatedMonthlyBillableHours: value.estimatedMonthlyBillableHours,
            estimatedMonthlyNonBillableHours: value.estimatedMonthlyNonBillableHours,
          })
        : pm,
    );

    setEditIndex(null);
    onChange(members);
  };

  const budget = calculateMonthlyBudget(project);

  const currency = project.currency;

  return (
    <Table small>
      <Table.BoxHeader>
        <Table.Column>Project Member</Table.Column>
        <Table.Column align="right" width="7.5rem" isVisible={project.isBillable}>
          Billable
        </Table.Column>
        <Table.Column align="right" width="7.5rem">
          Non-Billable
        </Table.Column>
        <Table.Column align="right" width="7.5rem" isVisible={project.isBillable && project.billingTypeId === 'tm'}>
          Rate
        </Table.Column>
        <Table.Column align="right" width="7.5rem" isVisible={project.isBillable && project.billingTypeId === 'tm'}>
          Revenue
        </Table.Column>
        <Table.BoxActionsColumn />
      </Table.BoxHeader>
      <Table.Body>
        {project.members.length > 0 && (
          <>
            {project.members.map((item, index) => (
              <MonthlyBudgetMemberRow
                key={item.id}
                project={project}
                projectMember={item}
                disableActions={editIndex !== null}
                isEditing={editIndex === index}
                currency={currency}
                onEdit={() => setEditIndex(index)}
                onCancel={() => setEditIndex(null)}
                onSaved={handleSave}
              />
            ))}
            <Table.Row style={{ borderBottom: 'none', fontWeight: weights.bold, textTransform: 'uppercase' }}>
              <Table.Cell />
              <Table.Cell>
                <Hours value={budget.billableHours} />
              </Table.Cell>
              <Table.Cell>
                <Hours value={budget.nonBillableHours} />
              </Table.Cell>
              <Table.Cell>
                <Currency value={budget.effectiveRate} currency={currency} />
              </Table.Cell>
              <Table.Cell>
                <Currency value={budget.revenue} currency={currency} />
              </Table.Cell>
              <Table.Cell />
            </Table.Row>
          </>
        )}
      </Table.Body>
    </Table>
  );
}

function MonthlyBudgetMemberRow({
  currency,
  project,
  projectMember,
  disableActions,
  isEditing,
  onEdit,
  onCancel,
  onSaved,
}) {
  async function handleSubmit(values) {
    onSaved(values);
  }

  if (!isEditing)
    return (
      <MonthlyBudgetMemberRowDetails
        project={project}
        projectMember={projectMember}
        disableActions={disableActions}
        currency={currency}
        onEdit={onEdit}
      />
    );

  return (
    <MonthlyBudgetMemberRowForm
      project={project}
      projectMember={projectMember}
      currency={currency}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    />
  );
}

function MonthlyBudgetMemberRowDetails({ currency, project, projectMember, disableActions, onEdit }) {
  const { member, isBillable, rate, estimatedMonthlyBillableHours, estimatedMonthlyNonBillableHours } = projectMember;
  const isActuallyBillable = project.isBillable && isBillable;
  const actualRate = isActuallyBillable ? rate : null;

  return (
    <Table.BoxRow>
      <Table.Cell>
        <BillableIcon value={isActuallyBillable} />
        {member.name}
      </Table.Cell>
      <Table.Cell>
        <Hours value={isBillable ? estimatedMonthlyBillableHours : null} minimumFractionDigits={0} />
      </Table.Cell>
      <Table.Cell>
        <Hours value={estimatedMonthlyNonBillableHours} minimumFractionDigits={0} />
      </Table.Cell>
      <Table.Cell>
        <Currency value={actualRate} maximumFractionDigits={7} currency={currency} />
      </Table.Cell>
      <Table.Cell>
        <Currency value={calculateRevenue(estimatedMonthlyBillableHours, actualRate)} currency={currency} />
      </Table.Cell>
      <TableBoxRowActions>
        <TableBoxRowActions.Edit disabled={disableActions} onClick={onEdit} />
      </TableBoxRowActions>
    </Table.BoxRow>
  );
}

function MonthlyBudgetMemberRowForm({
  project,
  projectMember,
  projectMember: { id, member, rate, isBillable },
  currency,
  onSubmit,
  onCancel,
}) {
  // Submit the row if the drawer's "Save" button is clicked
  const form = useRef();
  const { setForms } = useContext(MonthlyBudgetDrawerContext);

  useEffect(() => {
    // Register the inline form
    setForms((forms) => [...forms, { id: 'monthly_budget_member', ref: form }]);

    return () => {
      // Unregister the inline form
      setForms((forms) => forms.filter((f) => f.id !== 'monthly_budget_member'));
    };
  }, [setForms]);

  const initialValues = mergeValues(
    {
      id,
      estimatedMonthlyBillableHours: '',
      estimatedMonthlyNonBillableHours: '',
    },
    projectMember,
  );

  const isActuallyBillable = project.isBillable && isBillable;
  const actualRate = isActuallyBillable ? rate : null;

  return (
    <Formik
      innerRef={form}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={Yup.object().shape({
        estimatedMonthlyBillableHours: Yup.number().label('Billable Hours').min(0).max(9999.99).nullable(),
        estimatedMonthlyNonBillableHours: Yup.number().label('Non-Billable Hours').min(0).max(9999.99).nullable(),
      })}>
      {({ submitForm, values: { estimatedMonthlyBillableHours } }) => {
        return (
          <Table.BoxRow focused onEnter={submitForm}>
            <Table.Cell>
              <BillableIcon value={isActuallyBillable} />
              {member.name}
            </Table.Cell>
            <Table.Cell>
              {isActuallyBillable ? (
                <Field.Number
                  autoFocus={isActuallyBillable}
                  name="estimatedMonthlyBillableHours"
                  materialPlaceholder="Billable"
                  materialAlwaysVisible
                  min={0}
                  precision={2}
                />
              ) : (
                '-'
              )}
            </Table.Cell>
            <Table.Cell>
              <Field.Number
                autoFocus={!isActuallyBillable}
                name="estimatedMonthlyNonBillableHours"
                materialPlaceholder="Non-Billable"
                materialAlwaysVisible
                min={0}
                precision={2}
              />
            </Table.Cell>
            <Table.Cell>
              <Currency value={actualRate} maximumFractionDigits={7} currency={currency} />
            </Table.Cell>
            <Table.Cell>
              <Currency value={calculateRevenue(estimatedMonthlyBillableHours, actualRate)} currency={currency} />
            </Table.Cell>

            <TableBoxRowActions.Form onSubmit={submitForm} onCancel={onCancel} />
          </Table.BoxRow>
        );
      }}
    </Formik>
  );
}

export default MonthlyBudgetMembersTable;
